// src/sections/CMS/pages/CustomPageTemplate.tsx
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { getCustomCmsPageData } from '../api/cmsApi';

import LoadingScreen from '../../../components/shared/loading/LoadingScreen';
import MissingPage from '../../../components/shared/errorMessages/MissingPage';
import CustomPageBlockRenderer from '../components/customPage/CustomPageBlockRenderer';
import { captureAnalytics } from '../../../utilities/Analytics';

function CustomPageTemplate() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const {
    isSuccess,
    isError,
    isLoading,
    data: cmsResponse,
  } = useQuery(
    [`custom-page-${slug}`],
    () => getCustomCmsPageData(slug || ''),
    {
      staleTime: 600000,
      enabled: !!slug,
    }
  );

  const cmsPage = cmsResponse?.cms_page as any;
  const pageData = cmsPage?.page_data ?? null;
  const pageTitle = cmsResponse?.cms_category?.category_name ?? slug ?? '';
  const groupedCollections = cmsPage?.grouped_collections?.active ?? null;

  // multi_filter_fields lives on cms_category — passed down so the
  // Filtered Products block gets the full hub filter config
  const multiFilterFields = (cmsResponse?.cms_category as any)?.multi_filter_fields ?? null;

  // multi_filter_switches (e.g. only_new_arrival) also lives on cms_category —
  // one page-level switch set that scopes the Filtered Products grid.
  const multiFilterSwitches = (cmsResponse?.cms_category as any)?.multi_filter_switches ?? null;

  // Item Group Tiles block sources: the page's first parent-link category
  // (parent mode) and the page's own category id (child mode), plus per-tile
  // image overrides.
  const pageParentId = cmsPage?.parent_tile_data?.active?.[0]?.category_id ?? null;
  const pageOwnId = (cmsResponse?.cms_category as any)?.id ?? cmsPage?.category_id ?? null;
  const customChildTiles = cmsPage?.custom_child_tiles ?? null;

  // Resolved symbols for this page (top-level on the CMS response); Feature Badges
  // uses them to render the badge strip + modal.
  const symbols = (cmsResponse as any)?.symbols ?? null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isSuccess && pageTitle) {
      captureAnalytics('pageview', location.pathname, pageTitle);
    }
  }, [location.pathname, pageTitle, isSuccess]);

  if (!slug) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && pageData && (
        <CustomPageBlockRenderer
          pageData={pageData}
          pageTitle={pageTitle}
          groupedCollections={groupedCollections}
          multiFilterFields={multiFilterFields}
          multiFilterSwitches={multiFilterSwitches}
          pageParentId={pageParentId}
          pageOwnId={pageOwnId}
          customChildTiles={customChildTiles}
          symbols={symbols}
        />
      )}
      {isSuccess && !pageData && <MissingPage />}
    </>
  );
}

export default CustomPageTemplate;